import { combineReducers } from 'redux';
import login, * as fromLogin from './login';
import auth, * as fromAuth from './auth';
import newOrder, * as fromNewOrder from './neworder';
import addMedicine, * as fromAddMedicine from './addmedicine';
import searchGP, * as fromSearchGP from './searchgp';
import checkout, * as fromCheckout from './checkout';
import signup, { selectors as fromSignup } from './signup';
import user, * as fromUser from './user';
import intercom, * as fromIntercom from './intercom';
import pendingOrders, * as fromOrders from './orders';
import scheduling, * as fromScheduling from './scheduling';
import experiments, * as fromExperiments from './experiments';
import evidenceUpload, * as fromEvidenceUpload from './evidenceupload';
import device, * as fromDevice from './device';
import nms from './nms';

export const getNMSState = state => state.nms;

// authorised
export const getIsAuthenticated = (state) =>
  fromAuth.getIsAuthenticated(state.auth);
export const getAccessStartToken = (state) =>
  fromAuth.getAccessStartToken(state.auth);
export const getAccessToken = (state) => fromAuth.getAccessToken(state.auth);
export const getIdToken = (state) => fromAuth.getIdToken(state.auth);
export const getIdTokenExpiration = (state) =>
  fromAuth.getIdTokenExpiration(state.auth);
export const getRefreshToken = (state) => fromAuth.getRefreshToken(state.auth);
export const getRefreshTokenExpiration = (state) =>
  fromAuth.getRefreshTokenExpiration(state.auth);
export const getAuthUserId = (state) => fromAuth.getUserId(state.auth);
export const getAuthEmail = (state) => fromAuth.getEmail(state.auth);
export const getAuthPharmacyId = (state) => fromAuth.getPharmacyId(state.auth);

// login
export const getLoginEmail = (state) => fromLogin.getEmail(state.login);
export const getLoginError = (state) => fromLogin.getError(state.login);
export const getLoginEmailValidationMessage = (state) =>
  fromLogin.getEmailValidationMessage(state.login);
export const getLoginCodeValidationMessage = (state) =>
  fromLogin.getCodeValidationMessage(state.login);
export const getLoginShowAppPromotion = (state) =>
  fromLogin.getShowAppPromotion(state.login);

// new order
export const getNewOrderNotes = (state) =>
  fromNewOrder.getNotes(state.newOrder);
export const getNewOrderItems = (state) =>
  fromNewOrder.getItems(state.newOrder);
export const getNewOrderSelectedMedicine = (state) =>
  fromNewOrder.getSelectedMedicine(state.newOrder);
export const getNewOrderMedicineFormSelectedIndex = (state) =>
  fromNewOrder.getSelectedFormIndex(state.newOrder);
export const getNewOrderMedicineFormItem = (state) =>
  fromNewOrder.getSelectedMedicineFormItem(state.newOrder);
export const getNewOrderCreatingOrder = (state) =>
  fromNewOrder.getCreatingOrder(state.newOrder);
export const getNewOrderCreateError = (state) =>
  fromNewOrder.getCreateError(state.newOrder);
export const getNewOrderHasErrorAddingItem = (state) =>
  fromNewOrder.getHasErrorAddingItem(state.newOrder);
export const getCCPharmacySelected = (state) =>
  fromNewOrder.getCCPharmacySelected(state.newOrder);

// add medicine
export const getAddMedicineMedicines = (state) =>
  fromAddMedicine.getMedicines(state.addMedicine);
export const getAddMedicineMedicinesList = (state) =>
  fromAddMedicine.getMedicinesList(state.addMedicine);
export const getAddMedicineSearch = (state) =>
  fromAddMedicine.getSearchText(state.addMedicine);
export const getAddMedicineSelectedIndex = (state) =>
  fromAddMedicine.getSelectedIndex(state.addMedicine);
export const getAddMedicineHasNoResults = (state) =>
  fromAddMedicine.getHasNoResults(state.addMedicine);
export const getAddMedicineHasError = (state) =>
  fromAddMedicine.getHasError(state.addMedicine);

// search GP
export const getSearchGPGPs = (state) => fromSearchGP.getGPs(state.searchGP);
export const getV3User = (state) => state.user?.v3User;
export const getSearchGPSearch = (state) =>
  fromSearchGP.getSearchText(state.searchGP); // ( || state.user?.v3User?.gpDetails?.address?.postalCode)
export const getSearchGPSelectedIndex = (state) =>
  fromSearchGP.getSelectedIndex(state.searchGP);
export const getSearchGPHasNoResults = (state) =>
  fromSearchGP.getHasNoResults(state.searchGP);
export const getSearchGPHasError = (state) =>
  fromSearchGP.getHasError(state.searchGP);

// checkout
export const getCheckoutIsInvoiceSettled = (state) =>
  fromCheckout.getIsInvoiceSettled(state.checkout);
export const getCheckoutInvoice = (state) =>
  fromCheckout.getInvoice(state.checkout);
export const getCheckoutAmountOutstanding = (state) =>
  fromCheckout.getAmountOutstanding(state.checkout);
export const getCheckoutLineItems = (state) =>
  fromCheckout.getLineItems(state.checkout);
export const getCheckoutSelectedExemptionCode = (state) =>
  fromCheckout.getSelectedExemptionCode(state.checkout);
export const getCheckoutErrorSelectingExemptionCode = (state) =>
  fromCheckout.getErrorSelectingExemptionCode(state.checkout);
export const getCheckoutExemptionIsSkipped = (state) =>
  fromCheckout.getIsEvidenceSkipped(state.checkout);
export const getCheckoutSubmitDeclarationError = (state) =>
  fromCheckout.getSubmitDeclarationError(state.checkout);
export const getCheckoutIsSubmittingDeclaration = (state) =>
  fromCheckout.getIsSubmittingDeclaration(state.checkout);
export const getCheckoutInvoiceId = (state) =>
  fromCheckout.getInvoiceId(state.checkout);
export const getCheckoutOrderId = (state) =>
  fromCheckout.getOrderId(state.checkout);
export const getCheckoutIsSubmittable = (state) =>
  fromCheckout.getIsSubmittable(state.checkout);
export const getCheckoutSelectedExemptionNeedsEvidence = (state) =>
  fromCheckout.getSelectedExemptionNeedsEvidence(state.checkout);
export const getCheckoutInvoiceNeedsPaying = (state) =>
  fromCheckout.getInvoiceNeedsPaying(state.checkout);
export const getCheckoutEvidenceURL = (state) =>
  fromCheckout.getEvidenceURL(state.checkout);
export const getCheckoutPaymentError = (state) =>
  fromCheckout.getPaymentError(state.checkout);
export const getCheckoutBraintreeToken = (state) =>
  fromCheckout.getBraintreeToken(state.checkout);
export const getCheckboxIsVisible = (state) =>
  fromCheckout.getCheckbox(state.checkout);

// Signup
export const getSignupPassword = (state) =>
  fromSignup.getPassword(state.signup);
export const getSignupPasswordError = (state) =>
  fromSignup.getPasswordError(state.signup);
export const getSignupConfirmPassword = (state) =>
  fromSignup.getConfirmPassword(state.signup);
export const getSignupConfirmPasswordError = (state) =>
  fromSignup.getConfirmPasswordError(state.signup);

export const getSignupFirstName = (state) => {
  return (
    (state.signup.firstName && fromSignup.getFirstName(state.signup)) ||
    (state.user?.v3User?.firstName &&
      fromSignup.getFirstName(state.user.v3User)) ||
    null
  );
};
export const getSignupSurname = (state) => {
  return (
    (state.signup.surname && fromSignup.getSurname(state.signup)) ||
    state.user?.v3User?.lastName ||
    null
  );
};
export const getSignupGP = (state) => fromSignup.getGP(state.signup);
export const getSignupPhoneNumber = (state) => {
  return (
    (state.signup.phoneNumber && fromSignup.getPhoneNumber(state.signup)) ||
    state.user?.v3User?.phone ||
    null
  );
};
const formatDOB = (dob) => {
  const d = new Date(dob);
  return {
    day: `0${d.getDate()}`.slice(-2),
    month: `0${d.getMonth() + 1}`.slice(-2),
    year: d.getFullYear(),
  };
};
export const getSignupDOBYear = (state) => {
  return (
    fromSignup.getDOBYear(state.signup) ||
    (state.user?.v3User?.dob && formatDOB(state.user?.v3User?.dob).year) ||
    null
  );
};
export const getSignupDOBMonth = (state) =>
  fromSignup.getDOBMonth(state.signup) ||
  (state.user?.v3User?.dob && formatDOB(state.user?.v3User?.dob).month) ||
  null;
export const getSignupDOBDay = (state) =>
  fromSignup.getDOBDay(state.signup) ||
  (state.user?.v3User?.dob && formatDOB(state.user?.v3User?.dob).day) ||
  null;
export const getSignupDOBDate = (state) => fromSignup.getDOBDate(state.signup);
export const getSignupFirstNameError = (state) =>
  fromSignup.getFirstNameError(state.signup);
export const getSignupSurnameError = (state) =>
  fromSignup.getSurnameError(state.signup);
export const getSignupHasMarketingConsent = (state) =>
  fromSignup.getHasMarketingConsent(state.signup) ||
  state.user?.v3UserMarketingConsent?.hasMarketingConsent;
export const getSignupAddress = (state) => fromSignup.getAddress(state.signup);
export const getSignupAddressErrors = (state) =>
  fromSignup.getAddressErrors(state.signup);
export const getSignupAddressLine1 = (state) =>
  fromSignup.getAddressLine1(state.signup) ||
  state.user?.v3User?.address.address1 ||
  '';
export const getSignupAddressLine1Error = (state) =>
  fromSignup.getAddressLine1Error(state.signup);
export const getSignupAddressLine2Error = (state) =>
  fromSignup.getAddressLine2Error(state.signup);
export const getSignupAddressLine2 = (state) =>
  fromSignup.getAddressLine2(state.signup) ||
  state.user?.v3User?.address?.address2 ||
  '';
export const getSignupTownCity = (state) =>
  fromSignup.getTownCity(state.signup) ||
  state.user?.v3User?.address?.city ||
  '';
export const getSignupTownCityError = (state) =>
  fromSignup.getTownCityError(state.signup);
export const getSignupPostcode = (state) =>
  fromSignup.getPostcode(state.signup) ||
  state.user?.v3User?.address?.postalCode ||
  '';
export const getSignupPostcodeError = (state) =>
  fromSignup.getPostcodeError(state.signup);
export const getSignupGPError = (state) => fromSignup.getGPError(state.signup);
export const getSignupPhoneNumberError = (state) =>
  fromSignup.getPhoneNumberError(state.signup);
export const getSignupDOBError = (state) =>
  fromSignup.getDOBError(state.signup);
export const getSignupGPId = (state) => fromSignup.getGPId(state.signup); //  ) || 1
// export const getSignupGPId = (
//   state => fromSignup.getGPId(state.signup)
// );
// export const getSignupGPId = (
//   state => fromSignup.getGPId(state.signup)
// );
// export const getSignupGPId = (
//   state => fromSignup.getGPId(state.signup)
// );
// export const getSignupGPId = (
//   state => fromSignup.getGPId(state.signup)
// );

// User
export const getUserStatus = (state) => fromUser.getStatus(state.user);
export const getUserIsActive = (state) => fromUser.getIsActive(state.user);
export const getUserEmailVerified = (state) =>
  fromUser.getEmailVerified(state.user);
export const getUserIsApproved = (state) => fromUser.getIsApproved(state.user);
export const getUserIsSignedUp = (state) => fromUser.getIsSignedUp(state.user);
export const getUserUpdateError = (state) =>
  fromUser.getUpdateError(state.user);
export const getUserAssignedPharmacyId = (state) =>
  fromUser.getAssignedPharmacyId(state.user);
export const getUserHomeAddress = (state) =>
  fromUser.getHomeAddress(state.user);
export const getUserEmail = (state) => fromUser.getEmail(state.user);
export const getUserPhone = (state) => fromUser.getPhone(state.user);
export const getUserFirstName = (state) => fromUser.getFirstName(state.user);
export const getUserLastName = (state) => fromUser.getLastName(state.user);
export const getUserLoaded = (state) => fromUser.getLoaded(state.user);

// User delivery address
export const getUserDeliveryAddress = (state) =>
  fromUser.getDeliveryAddress(state.user);
export const getUserUpdateDeliveryAddressError = (state) =>
  fromUser.getUpdateDeliveryAddressError(state.user);
export const getUserDeliveryAddressLine1Error = (state) =>
  fromUser.getDeliveryAddressLine1Error(state.user);
export const getUserDeliveryAddressLine2Error = (state) =>
  fromUser.getDeliveryAddressLine2Error(state.user);
export const getUserDeliveryAddressTownCityError = (state) =>
  fromUser.getDeliveryTownCityError(state.user);
export const getUserDeliveryAddressPostcodeError = (state) =>
  fromUser.getDeliveryPostcodeError(state.user);

// Intercom
export const getIntercomUnreadCount = (state) =>
  fromIntercom.getUnreadCount(state.intercom);

// Orders
export const getOrdersList = (state) =>
  fromOrders.getOrders(state.pendingOrders);
export const getCCStoreList = (state) =>
  fromOrders.getClickAndCollect(state.pendingOrders);
export const getCCStoreError = (state) =>
  fromOrders.getHasErrorAddingItem(state.pendingOrders);

export const getAddCCStoreSelectedIndex = (state) =>
  fromOrders.getSelectedCCLocationIndex(state.pendingOrders);
export const getAddCCStoreSearch = (state) =>
  fromOrders.getSearchText(state.pendingOrders);

export const getConfirmDetailsList = (state) =>
  fromOrders.getConfirmDetails(state.pendingOrders);

export const getPendingOrdersList = (state) =>
  fromOrders.getPendingOrders(state.pendingOrders);
export const getPendingOrdersIsLoading = (state) =>
  fromOrders.getIsLoading(state.pendingOrders);
export const getPendingOrdersErrorMessage = (state) =>
  fromOrders.getErrorMessage(state.pendingOrders);
export const getPendingOrdersHasNoResults = (state) =>
  fromOrders.getHasNoResults(state.pendingOrders);
export const getPendingOrdersFromHomeReorder = (state) =>
  fromOrders.getFromHomeReorder(state.pendingOrders);

export const getPendingOrderDeliveryPreference = (state) =>
  fromOrders.getPendingOrderDeliveryPreference(state.pendingOrders);
export const getOrderSelectMessage = (state) =>
  fromOrders.getPaymentSummarySelectedMessage(state.pendingOrders);

export const getRMAddess = (state) =>
  fromOrders.getCCRMAddress(state.pendingOrders);

export const getPendingOrdersDeliveryAddressOrderId = (state) =>
  fromOrders.getDeliveryAddressOrderId(state.pendingOrders);
export const getPendingOrdersDeliveryPreferenceFlow = (state) =>
  fromOrders.getDeliveryPreferenceFlow(state.pendingOrders);
export const getPendingOrdersUpdateOrderDeliveryAddressError = (state) =>
  fromOrders.getUpdateOrderDeliveryAddressError(state.pendingOrders);
export const getPendingOrdersOrderDeliveryAddressLine1Error = (state) =>
  fromOrders.getOrderDeliveryAddressLine1Error(state.pendingOrders);
export const getPendingOrdersOrderDeliveryAddressLine2Error = (state) =>
  fromOrders.getOrderDeliveryAddressLine2Error(state.pendingOrders);
export const getPendingOrdersOrderDeliveryAddressTownCityError = (state) =>
  fromOrders.getOrderDeliveryAddressTownCityError(state.pendingOrders);
export const getPendingOrdersOrderDeliveryAddressPostcodeError = (state) =>
  fromOrders.getOrderDeliveryAddressPostcodeError(state.pendingOrders);
export const getPendingOrdersCurrentDeliveryAddressForOrder = (state) =>
  fromOrders.getCurrentDeliveryAddressForOrder(state.pendingOrders);
export const getPendingOrdersCurrentCCStoreAddressForOrder = (state) =>
  fromOrders.getCurrentCCStoreAddressForOrder(state.pendingOrders);
export const getCurrentOrderCCEligible = (state) =>
  fromOrders.getCurrentOrderCCEligible(state.pendingOrders);

export const getPendingOrderById = (state) =>
  fromOrders.getCurrentOrderById(state.pendingOrders);

// Scheduling
export const getSchedulingDisplayDate = (state) =>
  fromScheduling.getDisplayDate(state.scheduling);
export const getSchedulingSelectedOrderDate = (state) =>
  fromScheduling.getSelectedOrderDate(state.scheduling);
export const getSchedulingErrorMessage = (state) =>
  fromScheduling.getErrorMessage(state.scheduling);

// Experiments
export const getExperimentVariant = (state, name) =>
  fromExperiments.getExperimentVariant(state.experiments, name);

// Evidence Upload
export const getEvidenceUploadShowError = (state) =>
  fromEvidenceUpload.getShowError(state.evidenceUpload);
export const getEvidenceUploadErrorMessage = (state) =>
  fromEvidenceUpload.getErrorMessage(state.evidenceUpload);
export const getEvidenceUploadLockNextButton = (state) =>
  fromEvidenceUpload.getLockNextButton(state.evidenceUpload);

// Device
export const getDeviceIsMobile = (state) =>
  fromDevice.getIsMobile(state.device);

export default combineReducers({
  nms,
  login,
  newOrder,
  addMedicine,
  searchGP,
  auth,
  checkout,
  signup,
  user,
  intercom,
  pendingOrders,
  scheduling,
  experiments,
  evidenceUpload,
  device,
});
